import React, { useState } from 'react';
import { AuditLog } from '../types/audit';

interface ReportTableProps {
  data: AuditLog[];
  loading?: boolean;
}

const ReportTable: React.FC<ReportTableProps> = ({ data, loading }) => {
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [sortField, setSortField] = useState<keyof AuditLog>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: keyof AuditLog) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    // Handle undefined/null values
    if (aVal === undefined || aVal === null) aVal = sortDirection === 'asc' ? Infinity : -Infinity;
    if (bVal === undefined || bVal === null) bVal = sortDirection === 'asc' ? Infinity : -Infinity;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc' 
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }

    return 0;
  });

  const getStatusColor = (status: number): string => {
    if (status >= 200 && status < 300) return '#28a745'; // green
    if (status >= 400 && status < 500) return '#ffc107'; // yellow
    if (status >= 500) return '#dc3545'; // red
    return '#6c757d'; // gray
  };

  const formatCost = (cost?: number): string => {
    if (cost === undefined || cost === null) return '-';
    return `$${cost.toFixed(4)}`;
  };

  const formatTime = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatJSON = (jsonString: string): string => {
    try {
      // Try to parse and pretty-print the JSON
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If it's not valid JSON, return as-is
      return jsonString;
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading audit logs...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={styles.emptyState}>
        <p>No audit logs found for the selected filters.</p>
        <p style={styles.hint}>Try adjusting your filter criteria or date range.</p>
      </div>
    );
  }

  // Check if this is a summary report (has requestCount but no requestId)
  const isSummaryReport = data.length > 0 && 'requestCount' in data[0] && !('requestId' in data[0]);
  const isPerformanceReport = isSummaryReport && 'avgTimeMs' in data[0] && 'companyId' in data[0];

  // Render performance metrics table
  if (isPerformanceReport) {
    return (
      <div style={styles.container}>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Company</th>
                <th style={styles.th}>Requests</th>
                <th style={styles.th}>Avg Time</th>
                <th style={styles.th}>Median</th>
                <th style={styles.th}>P95</th>
                <th style={styles.th}>Max</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row: any, index) => (
                <tr key={index} style={styles.tr}>
                  <td style={{...styles.td, maxWidth: '300px', whiteSpace: 'normal'}}>
                    {row.companyName || row.companyId || '-'}
                  </td>
                  <td style={styles.td}>{row.requestCount}</td>
                  <td style={styles.td}>{row.avgTimeMs != null ? formatTime(row.avgTimeMs) : '-'}</td>
                  <td style={styles.td}>{row.medianTimeMs != null ? formatTime(row.medianTimeMs) : '-'}</td>
                  <td style={styles.td}>{row.p95TimeMs != null ? formatTime(row.p95TimeMs) : '-'}</td>
                  <td style={styles.td}>{row.maxTimeMs != null ? formatTime(row.maxTimeMs) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Render summary table for company/user reports
  if (isSummaryReport) {
    const hasCompanyId = 'companyId' in data[0];
    const hasUsername = 'username' in data[0];
    
    return (
      <div style={styles.container}>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>
                  {hasCompanyId ? 'Company ID' : 'Username'}
                </th>
                {hasCompanyId && <th style={styles.th}>Company Name</th>}
                <th style={styles.th}>API Requests</th>
                <th style={styles.th}>Total Requested</th>
                <th style={styles.th}>Total Processed</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row: any, index) => (
                <tr key={index} style={styles.tr}>
                  <td style={styles.td}>{hasCompanyId ? row.companyId : row.username}</td>
                  {hasCompanyId && <td style={styles.td}>{row.companyName || '-'}</td>}
                  <td style={styles.td}>{row.requestCount || '-'}</td>
                  <td style={styles.td}>{row.totalRequested || '-'}</td>
                  <td style={styles.td}>{row.totalProcessed || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Render full audit log table
  return (
    <div style={styles.container}>
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th onClick={() => handleSort('createdAt')} style={styles.th}>
                Created At {sortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('endpoint')} style={styles.th}>
                Endpoint {sortField === 'endpoint' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('method')} style={styles.th}>
                Method
              </th>
              <th onClick={() => handleSort('companyId')} style={styles.th}>
                Company ID
              </th>
              <th onClick={() => handleSort('companyName' as keyof AuditLog)} style={styles.th}>
                Company Name
              </th>
              <th onClick={() => handleSort('username')} style={styles.th}>
                User
              </th>
              <th onClick={() => handleSort('responseStatus')} style={styles.th}>
                Status {sortField === 'responseStatus' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('totalTime')} style={styles.th}>
                Total Time {sortField === 'totalTime' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('aiProcessingTime')} style={styles.th}>
                AI Time {sortField === 'aiProcessingTime' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th style={styles.th}>Cost</th>
              <th style={styles.th}>Model</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((log) => (
              <tr key={log.requestId} style={styles.tr}>
                <td style={styles.td}>{new Date(log.createdAt).toLocaleString()}</td>
                <td style={styles.td} title={log.endpoint}>{log.endpoint}</td>
                <td style={styles.td}>{log.method}</td>
                <td style={styles.td}>{log.companyId || '-'}</td>
                <td style={styles.td} title={log.companyName || ''}>{log.companyName || '-'}</td>
                <td style={styles.td}>{log.username || '-'}</td>
                <td style={{...styles.td, color: getStatusColor(log.responseStatus), fontWeight: 'bold'}}>
                  {log.responseStatus}
                </td>
                <td style={styles.td}>{log.totalTime ? formatTime(log.totalTime) : '-'}</td>
                <td style={styles.td}>{log.aiProcessingTime ? formatTime(log.aiProcessingTime) : '-'}</td>
                <td style={styles.td}>{formatCost(log.estimatedCost)}</td>
                <td style={styles.td} title={log.aiModel}>{log.aiModel?.split(':')[0] || '-'}</td>
                <td style={styles.td}>
                  <button
                    onClick={() => setSelectedLog(log)}
                    style={styles.detailButton}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal for detailed view */}
      {selectedLog && (
        <div style={styles.modal} onClick={() => setSelectedLog(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2>{selectedLog.method === 'PATCH' ? 'Repair Update Details' : 'Audit Log Details'}</h2>
              <button onClick={() => setSelectedLog(null)} style={styles.closeButton}>×</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.detailSection}>
                <h3>Request Information</h3>
                <p><strong>Request ID:</strong> {selectedLog.requestId}</p>
                <p><strong>Order ID:</strong> {selectedLog.orderId || '-'}</p>
                <p><strong>Created At:</strong> {new Date(selectedLog.createdAt).toLocaleString()}</p>
                <p><strong>Endpoint:</strong> {selectedLog.endpoint}</p>
                <p><strong>Method:</strong> {selectedLog.method}</p>
                <p><strong>Company ID:</strong> {selectedLog.companyId || '-'}</p>
                <p><strong>Company Name:</strong> {selectedLog.companyName || '-'}</p>
                <p><strong>Username:</strong> {selectedLog.username || '-'}</p>
                <p><strong>Locale:</strong> {selectedLog.locale || '-'}</p>
                <p><strong>Country:</strong> {selectedLog.country || '-'}</p>
              </div>

              <div style={styles.detailSection}>
                <h3>Response Information</h3>
                <p>
                  <strong>Status:</strong> 
                  <span style={{color: getStatusColor(selectedLog.responseStatus), fontWeight: 'bold', marginLeft: '8px'}}>
                    {selectedLog.responseStatus}
                  </span>
                </p>
                {selectedLog.method !== 'PATCH' && (
                  <>
                    <p><strong>Total Time:</strong> {selectedLog.totalTime ? formatTime(selectedLog.totalTime) : '-'}</p>
                    <p><strong>AI Processing Time:</strong> {selectedLog.aiProcessingTime ? formatTime(selectedLog.aiProcessingTime) : '-'}</p>
                  </>
                )}
              </div>

              {selectedLog.method !== 'PATCH' && (
                <div style={styles.detailSection}>
                  <h3>AI Information</h3>
                  <p><strong>Provider:</strong> {selectedLog.aiProvider || '-'}</p>
                  <p><strong>Model:</strong> {selectedLog.aiModel || '-'}</p>
                  <p><strong>Input Tokens:</strong> {selectedLog.inputTokens || '-'}</p>
                  <p><strong>Output Tokens:</strong> {selectedLog.outputTokens || '-'}</p>
                  <p><strong>Estimated Cost:</strong> {formatCost(selectedLog.estimatedCost)}</p>
                </div>
              )}

              {selectedLog.method === 'PATCH' && selectedLog.requestBody && (() => {
                try {
                  const body = typeof selectedLog.requestBody === 'string' ? JSON.parse(selectedLog.requestBody) : selectedLog.requestBody;
                  return (
                    <div style={styles.detailSection}>
                      <h3>Repair Update (Request)</h3>
                      <table style={{...styles.table, border: '1px solid #d8dde6', borderRadius: '6px'}}>
                        <tbody>
                          <tr style={styles.tr}>
                            <td style={{...styles.patchLabel}}>Repair ID</td>
                            <td style={{...styles.patchValue}}>{body.id || '-'}</td>
                          </tr>
                          <tr style={styles.tr}>
                            <td style={{...styles.patchLabel}}>Title</td>
                            <td style={{...styles.patchValue}}>{body.title || '-'}</td>
                          </tr>
                          <tr style={styles.tr}>
                            <td style={{...styles.patchLabel}}>Summary</td>
                            <td style={{...styles.patchValue}}>{body.summary || <span style={{color: '#8a96a8', fontStyle: 'italic'}}>Not provided</span>}</td>
                          </tr>
                          <tr style={styles.tr}>
                            <td style={{...styles.patchLabel}}>Severity</td>
                            <td style={{...styles.patchValue}}>
                              {body.severity ? (
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  color: 'white',
                                  backgroundColor: body.severity === 'RED' ? '#dc3545' : body.severity === 'AMBER' ? '#f0950c' : body.severity === 'GREEN' ? '#28a745' : '#6c757d',
                                }}>
                                  {body.severity}
                                </span>
                              ) : <span style={{color: '#8a96a8', fontStyle: 'italic'}}>Not provided</span>}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                } catch {
                  return (
                    <div style={styles.detailSection}>
                      <h3>Request Body</h3>
                      <pre style={styles.pre}>{formatJSON(selectedLog.requestBody)}</pre>
                    </div>
                  );
                }
              })()}

              {selectedLog.method === 'PATCH' && selectedLog.responseBody && (() => {
                try {
                  const body = typeof selectedLog.responseBody === 'string' ? JSON.parse(selectedLog.responseBody) : selectedLog.responseBody;
                  return (
                    <div style={styles.detailSection}>
                      <h3>Update Result (Response)</h3>
                      <table style={{...styles.table, border: '1px solid #d8dde6', borderRadius: '6px'}}>
                        <tbody>
                          <tr style={styles.tr}>
                            <td style={{...styles.patchLabel}}>Message</td>
                            <td style={{...styles.patchValue, color: selectedLog.responseStatus < 300 ? '#28a745' : '#dc3545', fontWeight: '600'}}>
                              {body.message || '-'}
                            </td>
                          </tr>
                          <tr style={styles.tr}>
                            <td style={{...styles.patchLabel}}>Repair ID</td>
                            <td style={{...styles.patchValue}}>{body.repairId || '-'}</td>
                          </tr>
                          <tr style={styles.tr}>
                            <td style={{...styles.patchLabel}}>Timestamp</td>
                            <td style={{...styles.patchValue}}>{body.timestamp ? new Date(body.timestamp).toLocaleString() : '-'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                } catch {
                  return (
                    <div style={styles.detailSection}>
                      <h3>Response Body</h3>
                      <pre style={styles.pre}>{formatJSON(selectedLog.responseBody)}</pre>
                    </div>
                  );
                }
              })()}

              {selectedLog.method !== 'PATCH' && selectedLog.requestBody && (
                <div style={styles.detailSection}>
                  <h3>Request Body</h3>
                  <pre style={styles.pre}>{formatJSON(selectedLog.requestBody)}</pre>
                </div>
              )}

              {selectedLog.method !== 'PATCH' && selectedLog.responseBody && (
                <div style={styles.detailSection}>
                  <h3>Response Body</h3>
                  <pre style={styles.pre}>{formatJSON(selectedLog.responseBody)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    marginTop: '0',
  },
  tableContainer: {
    overflowX: 'auto',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #d8dde6',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    padding: '10px 14px',
    textAlign: 'left',
    backgroundColor: '#f4f6f9',
    borderBottom: '2px solid #d8dde6',
    fontWeight: '600',
    fontSize: '11px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: '#5a6a80',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid #eef1f6',
  },
  td: {
    padding: '10px 14px',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#1a2332',
  },
  detailButton: {
    padding: '3px 10px',
    backgroundColor: '#0055a5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.02em',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    color: '#5a6a80',
  },
  spinner: {
    width: '36px',
    height: '36px',
    border: '3px solid #eef1f6',
    borderTop: '3px solid #0055a5',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#5a6a80',
  },
  hint: {
    fontSize: '13px',
    color: '#8a96a8',
    marginTop: '8px',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(13,31,60,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #d8dde6',
    backgroundColor: '#f4f6f9',
    borderRadius: '10px 10px 0 0',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#8a96a8',
    lineHeight: 1,
    padding: '0',
  },
  modalBody: {
    padding: '20px',
  },
  detailSection: {
    marginBottom: '20px',
  },
  pre: {
    backgroundColor: '#f4f6f9',
    padding: '14px',
    borderRadius: '6px',
    overflow: 'auto',
    maxHeight: '400px',
    fontSize: '12px',
    lineHeight: '1.6',
    border: '1px solid #d8dde6',
    fontFamily: "'SFMono-Regular', Consolas, 'Courier New', monospace",
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#1a2332',
  },
  patchLabel: {
    padding: '10px 14px',
    fontWeight: '600',
    color: '#5a6a80',
    backgroundColor: '#f4f6f9',
    width: '140px',
    fontSize: '13px',
    borderRight: '1px solid #d8dde6',
    whiteSpace: 'nowrap',
  },
  patchValue: {
    padding: '10px 14px',
    color: '#1a2332',
    fontSize: '13px',
    wordBreak: 'break-word',
  },
};

export default ReportTable;

